import { NgComponentOutlet } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Type, ViewChild } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSidenavModule } from "@angular/material/sidenav";
import { Cluster, MarkerClusterer } from "@googlemaps/markerclusterer";
import { DefaultComponent } from "src/app/default.component";
import { ORGANISATION } from "src/organisation/organisation";
import { v4 as uuid } from "uuid";
import { PrefixTemplate } from "../../PrefixTemplate";
import { MaxLength } from "../../types/MaxLength";
import { MinLength } from "../../types/MinLength";
import { DefaultMarkerDisplayComponent } from "./default-marker-display/default-marker-display.component";

export type Coordinate = google.maps.LatLngLiteral | google.maps.LatLng | google.maps.LatLngAltitudeLiteral;

export interface MapOptions {
  map?: google.maps.MapOptions;
  marker?: google.maps.marker.AdvancedMarkerElement;
}

export interface MarkerData<T = unknown> {
  id: string;
  location: Coordinate;
  data: T | null;
}

@Component({
  selector: "app-template-map",
  imports: [ReactiveFormsModule, GoogleMapsModule, MatButtonModule, MatIconModule, MatSidenavModule, NgComponentOutlet],
  templateUrl: "./template-map.component.html",
  styleUrl: "./template-map.component.less",
})
export class TemplateMapComponent extends DefaultComponent implements PrefixTemplate<MarkerData[]>, OnInit, AfterViewInit, OnChanges {
  @Input({ required: true })
  public control: FormControl<MarkerData[] | null>;

  @Input()
  public label: string | null;

  @Input()
  public polygon: boolean;

  @Input()
  public options: MapOptions | null;

  @Input()
  public value: MarkerData[] | null;

  @Input()
  public minlength: MinLength;

  @Input()
  public maxlength: MaxLength;

  @Input()
  public required: boolean;

  @Input()
  public disabled: boolean;

  @Input()
  public mode: "view" | "edit";

  @Output()
  public event: EventEmitter<null>;

  @Input()
  public markerDisplayComponent: Type<DefaultMarkerDisplayComponent>;

  @ViewChild("container")
  public container: ElementRef<HTMLDivElement> | null;

  public map: google.maps.Map | null;
  public mapMarkers: google.maps.marker.AdvancedMarkerElement[];
  public mapPolygon: google.maps.Polygon;
  public mapOptions: google.maps.MapOptions;
  public markerOptions: google.maps.marker.AdvancedMarkerElementOptions;
  private clusterer: MarkerClusterer;

  public selectedMarkers: MarkerData[] | null;
  public selectedMarkerPin: google.maps.marker.AdvancedMarkerElement | null;

  private maxZoom: number;

  public constructor() {
    super();
    this.control = new FormControl();
    this.value = null;
    this.minlength = null;
    this.maxlength = null;
    this.required = false;
    this.disabled = false;
    this.mode = "view";
    this.maxZoom = 18;

    this.container = null;
    this.map = null;
    this.selectedMarkers = null;
    this.selectedMarkerPin = null;

    this.polygon = false;
    this.mapMarkers = [];
    this.mapPolygon = new google.maps.Polygon({
      strokeColor: "#000000",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#000000",
      fillOpacity: 0.35,
    });
    this.markerDisplayComponent = DefaultMarkerDisplayComponent;
    this.clusterer = new MarkerClusterer({ algorithmOptions: { maxZoom: this.maxZoom } });
    this.clusterer.onClusterClick = (event, cluster, map): void => {
      const zoom = map.getZoom();
      if (zoom !== undefined && zoom + 3 >= this.maxZoom) {
        this.onClusterClick(cluster);
      } else {
        new MarkerClusterer({ algorithmOptions: { maxZoom: this.maxZoom } }).onClusterClick(event, cluster, map);
      }
    };

    this.options = null;
    this.label = null;

    if (!ORGANISATION.MAP) throw new Error("Cannot use template-map without map config in organisation!");

    this.mapOptions = {
      zoom: 7,
      streetViewControl: false,
      mapTypeControl: false,
      mapId: ORGANISATION.MAP.ID,
      isFractionalZoomEnabled: true,
      maxZoom: this.maxZoom,
    };

    this.markerOptions = {};

    this.event = new EventEmitter();
  }

  public ngOnChanges(): void {
    if (this.map) {
      this.setMarkers();
    }
  }

  public ngOnInit(): void {
    const control = this.control;
    if (control) {
      this.addValidators(control);
    } else {
      throw new Error("Undefined control");
    }

    this.addSubscription(
      this.control.valueChanges.subscribe(() => {
        if (this.map) {
          this.setMarkers();
        }
      }),
    );

    this.mapOptions = {
      ...this.mapOptions,
      ...this.options?.map,
    };

    this.markerOptions = {
      ...this.markerOptions,
      ...this.options?.marker,
    };
  }

  public async ngAfterViewInit(): Promise<void> {
    if (this.container) {
      const map = new google.maps.Map(this.container.nativeElement, this.mapOptions);
      this.map = map;
      this.mapPolygon.setMap(map);
      this.setMarkers();

      if (this.mode === "edit") {
        this.map.addListener("dblclick", (e: google.maps.MapMouseEvent) => this.onMapEditDoubleClick(e));
      } else {
        this.clusterer.setMap(map);
      }

      this.moveTo(this.control?.value ?? []);
    } else {
      throw new Error("Could not find map container.");
    }
  }

  public onMapEditDoubleClick({ latLng }: google.maps.MapMouseEvent): void {
    if (latLng) {
      if (this.polygon) {
        this.control.setValue([...(this.control.value ?? []), { data: null, id: uuid(), location: latLng }]);
      } else {
        this.control.setValue([{ data: null, location: latLng, id: uuid() }]);
      }
      this.control.markAsDirty();
    }
  }

  public onMarkerEditClick(id: string, { latLng }: google.maps.MapMouseEvent): void {
    if (latLng) {
      if (this.polygon) {
        this.control.setValue(this.control.value?.filter((x) => x.id !== id) ?? null);
      } else {
        this.control.setValue(null);
      }
      this.control.markAsDirty();
    }
  }

  public onMarkerEditDragEnd(id: string, { latLng }: google.maps.MapMouseEvent): void {
    if (latLng) {
      if (this.polygon) {
        this.control.setValue(
          this.control.value?.map((m) => {
            if (m.id === id) {
              return { data: null, id: uuid(), location: latLng };
            } else {
              return m;
            }
          }) ?? null,
        );
      } else {
        this.control.setValue([{ data: null, location: latLng, id: uuid() }]);
      }
      this.control.markAsDirty();
    }
  }

  // TODO: Marker click delete
  // TODO: Marker drag end to edit
  // TODO: Adapt polygon => Maybe automaticly handled by setmarkers??

  public onMarkerViewClick(element: google.maps.marker.AdvancedMarkerElement | null, context: MarkerData[]): void {
    this.closeMarker();

    if (element !== null) {
      const activePin = new google.maps.marker.PinElement({ scale: 1.25 });
      if (element.position) {
        element.content = activePin.element;
        this.selectedMarkerPin = element;
      } else {
        throw new Error("Could not get marker position.");
      }
    }
    this.selectedMarkers = context;
  }

  public onClusterClick(element: Cluster): void {
    const markers = element.markers;
    if (markers) {
      const clusterData: MarkerData[] = [];
      for (const _ of markers) {
        const marker = <google.maps.marker.AdvancedMarkerElement>_;
        const position = marker.position;
        if (position) {
          const lat = typeof position.lat === "number" ? position.lat : position.lat();
          const lng = typeof position.lng === "number" ? position.lng : position.lng();
          const x = this.control?.value?.find((m) => {
            const _lat = typeof m.location.lat === "number" ? m.location.lat : m.location.lat();
            const _lng = typeof m.location.lng === "number" ? m.location.lng : m.location.lng();
            return !clusterData.includes(m) && lat === _lat && _lng === lng;
          });
          if (x) {
            clusterData.push(x);
          }
        }
      }
      this.onMarkerViewClick(null, clusterData);
    }
  }

  public closeMarker(): void {
    if (this.selectedMarkers) {
      this.selectedMarkers = null;
    }
    if (this.selectedMarkerPin) {
      this.selectedMarkerPin.content = null;
    }
  }

  private moveTo(markers: MarkerData[]): void {
    if (this.map && markers.length) {
      const bounds = new google.maps.LatLngBounds();
      for (const marker of markers) {
        bounds.extend(marker.location);
      }
      this.map.fitBounds(bounds, 80);
    } else if (this.map) {
      this.map.setCenter(new google.maps.LatLng(0, 0));
      this.map.setZoom(2);
    }
  }

  private addValidators(control: FormControl<unknown | null>): void {
    control.updateValueAndValidity();
  }

  private setMarkers(): void {
    this.closeMarker();
    if (this.mode === "view") this.clusterer.removeMarkers(this.mapMarkers);
    if (this.mode === "edit") {
      this.mapMarkers.forEach((m) => (m.map = null));
    }
    console.log(this.control.value);
    this.mapMarkers =
      this.control.value?.map((context: MarkerData) => {
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: context.location,
          map: this.mode === "edit" && this.map ? this.map : undefined,
          gmpDraggable: this.mode === "edit",
        });
        if (this.mode === "view" && context.data !== null) marker.addListener("click", () => this.onMarkerViewClick(marker, [context]));
        if (this.mode === "edit") {
          marker.addListener("dragend", (e: google.maps.MapMouseEvent) => this.onMarkerEditDragEnd(context.id, e));
          marker.addListener("click", (e: google.maps.MapMouseEvent) => this.onMarkerEditClick(context.id, e));
        }
        return marker;
      }) ?? [];

    if (this.mode === "view") this.clusterer.addMarkers(this.mapMarkers);

    if (this.polygon && this.map) {
      this.mapPolygon.setPaths(this.control.value?.map((x) => x.location) ?? []);
    }

    this.moveTo(this.control.value ?? []);
  }
}
